import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { CapicheLoggerService } from 'capiche-logger';
import { CapicheMaterialModule } from 'capiche-material';
import { DisclosurePath } from 'capiche-disclosure-path';
import { TextToSpeechService } from 'capiche-text-processor';

@Component({
  selector: 'app-disclosure-controls',
  standalone: true,
  imports: [CommonModule, CapicheMaterialModule],
  templateUrl: './disclosure-controls.component.html',
  styleUrls: ['./disclosure-controls.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DisclosureControlsComponent implements OnDestroy, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  private speechService = inject(TextToSpeechService);
  private logger = new CapicheLoggerService('disclosure-controls:', false);

  // Inputs
  @Input({ required: true }) thisItemPath!: DisclosurePath;
  @Input() speakText = '';
  @Input() extendedSpeakText = '';
  @Input() readDefinitionWhenExpanded = true;
  @Input() expanded = false; // parent tells us when its panel is expanded

  // Outputs
  @Output() requestReplace = new EventEmitter<DisclosurePath>();

  // Local state
  replaceIcon = 'check_circle_outline';
  replaceIsHovered = false;

  private currentSpeakId: string | null = null;
  private endSub?: Subscription;
  private queuedDefinitionForCurrent = false;
  isSpeaking = false;

  ngOnDestroy() {
    this.endSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['expanded']) {
      const was = changes['expanded'].previousValue;
      const now = changes['expanded'].currentValue;

      if (was && !now && this.isSpeaking) {
        // panel just closed → stop audio
        this.onStopAudio();
      }

      if (
        !was &&
        now &&
        this.isSpeaking &&
        this.currentSpeakId &&
        this.readDefinitionWhenExpanded &&
        !this.queuedDefinitionForCurrent
      ) {
        const def = this.extendedSpeakText?.trim();
        if (def) {
          this.speechService.speak(def, { id: this.currentSpeakId, interrupt: false });
          this.queuedDefinitionForCurrent = true;
          this.logger.log('ngOnChanges: queued definition on expand');
        }
      }
    }
  }

  /** Build TTS content */
  private buildSpeechText(): {
    title: string;
    definition?: string;
    includeDefinition: boolean;
  } {
    const title = this.speakText?.trim() || '';
    const def = this.extendedSpeakText?.trim();
    const includeDefinition = !!this.readDefinitionWhenExpanded && this.expanded && !!def;
    return { title, definition: def || undefined, includeDefinition };
  }

  /** Start speaking */
  onStartAudio(e: Event) {
    e.stopPropagation();
    this.logger.log('onStartAudio: start', { expanded: this.expanded });

    const { title, definition, includeDefinition } = this.buildSpeechText();
    const id = this.speechService.speakSuggestionAndMaybeDefinition(title, definition, {
      includeDefinition,
      interrupt: true,
    });

    this.currentSpeakId = id;
    this.isSpeaking = true;
    this.queuedDefinitionForCurrent = includeDefinition;

    this.logger.log('onStartAudio: queued', {
      id,
      includeDefinition,
      queuedDefinitionForCurrent: this.queuedDefinitionForCurrent,
    });

    this.watchEndFor(id);
    this.cdr.markForCheck();
  }

  /** Stop speaking */
  onStopAudio(e?: Event) {
    e?.stopPropagation();
    this.logger.log('onStopAudio', { currentSpeakId: this.currentSpeakId });

    if (this.currentSpeakId) this.speechService.stop(this.currentSpeakId);
    else this.speechService.stopAll();
    this.resetTtsState();
    this.cdr.markForCheck();
  }

  /** Replacement */
  makeReplacement(e: Event) {
    e.stopPropagation();
    if (this.isSpeaking) this.onStopAudio();
    this.logger.log('makeReplacement', { path: this.thisItemPath });
    this.requestReplace.emit(this.thisItemPath);
  }

  // ---- internal helpers ----
  private watchEndFor(id: string) {
    this.endSub?.unsubscribe();
    this.endSub = this.speechService.ended$(id).subscribe(() => {
      this.resetTtsState();
    });
    this.cdr.markForCheck();
  }

  private resetTtsState() {
    this.endSub?.unsubscribe();
    this.currentSpeakId = null;
    this.isSpeaking = false;
    this.queuedDefinitionForCurrent = false;
    this.logger.log('resetTtsState');
    this.cdr.markForCheck();
  }
}
